import { Building2, ChevronsUpDown, Shield, TrendingUp, User } from "lucide-react";
import type * as React from "react";
import { useNavigate } from "react-router";

import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import {
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	useSidebar,
} from "~/components/ui/sidebar";

type UserRole = "borrower" | "servicer_staff" | "originator_staff" | "investor";

const roleConfig: Record<
	UserRole,
	{ name: string; icon: React.ElementType; description: string; baseUrl: string }
> = {
	borrower: {
		baseUrl: "/borrower",
		description: "Manage your loans",
		icon: User,
		name: "Borrower",
	},
	investor: {
		baseUrl: "/investor",
		description: "Track investments",
		icon: TrendingUp,
		name: "Investor",
	},
	originator_staff: {
		baseUrl: "/originator",
		description: "View your portfolio",
		icon: Building2,
		name: "Originator",
	},
	servicer_staff: {
		baseUrl: "/servicer",
		description: "Manage operations",
		icon: Shield,
		name: "Servicer",
	},
};

export function RoleSwitcher({
	roles,
	activeRole,
	onRoleChange,
}: {
	roles: UserRole[];
	activeRole: UserRole;
	onRoleChange: (role: UserRole) => void;
}) {
	const { isMobile } = useSidebar();
	const navigate = useNavigate();
	const currentRole = roleConfig[activeRole];

	if (!currentRole || roles.length <= 1) {
		return null;
	}

	const handleRoleChange = (role: UserRole) => {
		// Update local state
		onRoleChange(role);

		// Set cookie for persistence
		document.cookie = `active-role=${role}; path=/; max-age=31536000`; // 1 year

		// Navigate to the base URL for the new role
		const targetUrl = roleConfig[role].baseUrl;
		navigate(targetUrl);
	};

	return (
		<SidebarMenu>
			<SidebarMenuItem>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<SidebarMenuButton
							className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
							size="lg"
						>
							<div className="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
								<currentRole.icon className="size-4" />
							</div>
							<div className="grid flex-1 text-left text-sm leading-tight">
								<span className="truncate font-medium">{currentRole.name}</span>
								<span className="truncate text-xs">
									{currentRole.description}
								</span>
							</div>
							<ChevronsUpDown className="ml-auto" />
						</SidebarMenuButton>
					</DropdownMenuTrigger>
					<DropdownMenuContent
						align="start"
						className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
						side={isMobile ? "bottom" : "right"}
						sideOffset={4}
					>
						<DropdownMenuLabel className="text-muted-foreground text-xs">
							Switch Role
						</DropdownMenuLabel>
						{roles.map((role) => {
							const config = roleConfig[role];
							return (
								<DropdownMenuItem
									className="gap-2 p-2"
									key={role}
									onClick={() => handleRoleChange(role)}
								>
									<div className="flex size-6 items-center justify-center rounded-md border">
										<config.icon className="size-3.5 shrink-0" />
									</div>
									<div className="grid flex-1">
										<span className="font-medium">{config.name}</span>
										<span className="text-xs text-muted-foreground">
											{config.description}
										</span>
									</div>
								</DropdownMenuItem>
							);
						})}
					</DropdownMenuContent>
				</DropdownMenu>
			</SidebarMenuItem>
		</SidebarMenu>
	);
}
